import type { PublicResult, SprintChartData, SprintChartPoint } from '../types/result';

interface SprintLineChartProps {
  data: SprintChartData;
  professionalBenchmarks: PublicResult[];
}

const width = 640;
const height = 360;
const margin = { top: 24, right: 24, bottom: 52, left: 58 };
const distanceTicks = [0, 10, 20, 30, 40];
const professionalColors = ['#00a1c9', '#1d8102'];

function benchmarkName(result: PublicResult): string {
  return result.benchmarkLabel ??
    [result.firstName, result.lastName].filter(Boolean).join(' ');
}

function sprintPoints(result: PublicResult): SprintChartPoint[] {
  return [
    { distanceYards: 10, timeSeconds: result.sprint10Seconds },
    { distanceYards: 20, timeSeconds: result.sprint20Seconds },
    { distanceYards: 30, timeSeconds: result.sprint30Seconds },
    { distanceYards: 40, timeSeconds: result.sprint40Seconds },
  ].flatMap(({ distanceYards, timeSeconds }) =>
    timeSeconds === null ? [] : [{ distanceYards, timeSeconds }],
  );
}

function linePoints(
  points: SprintChartPoint[],
  xPosition: (value: number) => number,
  yPosition: (value: number) => number,
): string {
  return [{ timeSeconds: 0, distanceYards: 0 }, ...points]
    .map((point) => `${xPosition(point.timeSeconds)},${yPosition(point.distanceYards)}`)
    .join(' ');
}

export function SprintLineChart({
  data,
  professionalBenchmarks,
}: SprintLineChartProps) {
  const professionalSeries = professionalBenchmarks
    .map((benchmark, index) => ({
      label: `Pro: ${benchmarkName(benchmark)}`,
      points: sprintPoints(benchmark),
      color: professionalColors[index % professionalColors.length],
      dashArray: '3 5',
    }))
    .filter((series) => series.points.length > 0);
  const allPoints = [
    ...data.athlete,
    ...data.participantAverage,
    ...professionalSeries.flatMap((series) => series.points),
  ];
  if (allPoints.length === 0) {
    return (
      <p className="py-12 text-center text-sm font-bold text-aws-slate">
        40-yard dash comparison data is not available.
      </p>
    );
  }

  const maximumTime = Math.max(...allPoints.map((point) => point.timeSeconds)) * 1.08;
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const xPosition = (timeSeconds: number) =>
    margin.left + (timeSeconds / maximumTime) * plotWidth;
  const yPosition = (distanceYards: number) =>
    margin.top + plotHeight - (distanceYards / 40) * plotHeight;
  const timeTicks = Array.from({ length: 6 }, (_, index) => (maximumTime * index) / 5);

  const series = [
    {
      label: 'Your run',
      points: data.athlete,
      color: '#ff9900',
      dashArray: undefined,
    },
    {
      label: 'Participant average',
      points: data.participantAverage,
      color: '#5f45ba',
      dashArray: '8 6',
    },
    ...professionalSeries,
  ];

  return (
    <div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full"
        role="img"
        aria-labelledby="sprint-chart-title sprint-chart-description"
      >
        <title id="sprint-chart-title">40-yard dash milestone comparison</title>
        <desc id="sprint-chart-description">
          Your time to each distance milestone compared with the event participant average and
          professional athletes.
        </desc>

        {timeTicks.map((tick) => (
          <g key={`time-${tick}`}>
            <line
              x1={xPosition(tick)}
              y1={margin.top}
              x2={xPosition(tick)}
              y2={margin.top + plotHeight}
              stroke="currentColor"
              className="text-indigo-100"
            />
            <text
              x={xPosition(tick)}
              y={margin.top + plotHeight + 20}
              textAnchor="middle"
              className="fill-aws-slate text-[11px]"
            >
              {tick.toFixed(1)}
            </text>
          </g>
        ))}
        {distanceTicks.map((tick) => (
          <g key={`distance-${tick}`}>
            <line
              x1={margin.left}
              y1={yPosition(tick)}
              x2={width - margin.right}
              y2={yPosition(tick)}
              stroke="currentColor"
              className="text-indigo-100"
            />
            <text
              x={margin.left - 10}
              y={yPosition(tick)}
              dy="0.35em"
              textAnchor="end"
              className="fill-aws-slate text-[11px]"
            >
              {tick}
            </text>
          </g>
        ))}

        {series.map((item) => (
          <g key={item.label}>
            {item.points.length > 0 && (
              <polyline
                points={linePoints(item.points, xPosition, yPosition)}
                fill="none"
                stroke={item.color}
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray={item.dashArray}
              />
            )}
            {item.points.map((point) => (
              <circle
                key={point.distanceYards}
                cx={xPosition(point.timeSeconds)}
                cy={yPosition(point.distanceYards)}
                r="6"
                fill={item.color}
                stroke="white"
                strokeWidth="2"
              >
                <title>
                  {item.label}: {point.distanceYards} yards in {point.timeSeconds.toFixed(2)} seconds
                </title>
              </circle>
            ))}
          </g>
        ))}
        <circle
          cx={xPosition(0)}
          cy={yPosition(0)}
          r="6"
          fill="#232f3e"
          stroke="white"
          strokeWidth="2"
          aria-label="Start: 0 yards at 0 seconds"
        >
          <title>Start: 0 yards at 0 seconds</title>
        </circle>

        <text
          x={margin.left + plotWidth / 2}
          y={height - 10}
          textAnchor="middle"
          className="fill-aws-slate text-[13px] font-bold"
        >
          Time (seconds)
        </text>
        <text
          x={16}
          y={margin.top + plotHeight / 2}
          textAnchor="middle"
          transform={`rotate(-90 16 ${margin.top + plotHeight / 2})`}
          className="fill-aws-slate text-[13px] font-bold"
        >
          Distance (yards)
        </text>
      </svg>

      <div className="mt-2 flex flex-wrap items-center justify-center gap-5 text-xs font-bold text-aws-slate">
        {series.map((item) => (
          <span key={item.label} className="flex items-center gap-2">
            <span className="h-1 w-5 rounded-full" style={{ backgroundColor: item.color }} />
            {item.label}
          </span>
        ))}
      </div>
    </div>
  );
}
